import {UpperCasePipe} from "@angular/common"
import {Component, computed, input, OnDestroy, signal, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatOptionModule} from "@angular/material/core"
import {MatInputModule} from "@angular/material/input"
import {MatSelect} from "@angular/material/select"
import {MatTooltipModule} from "@angular/material/tooltip"
import {LoadingSpinnerIconComponent} from "@app/common/components/progress"
import {PbrExport, MaterialMapsExporterCore} from "@cm/material-maps-exporter"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-output-custom-pbr-export-form",
    imports: [FormsModule, LoadingSpinnerIconComponent, MatInputModule, MatOptionModule, MatSelect, MatTooltipModule, UpperCasePipe],
    templateUrl: "./material-output-custom-pbr-export-form.component.html",
    styleUrl: "./material-output-custom-pbr-export-form.component.scss",
})
export class MaterialOutputCustomPbrExportFormComponent implements OnDestroy {
    readonly $onSubmit = input.required<(settings: PbrExport.Types.Settings) => Promise<void>>({alias: "onSubmit"})
    /** The settings of the material's existing custom exports, so the form can say so before submitting a duplicate. */
    readonly $existingSettings = input<PbrExport.Types.Settings[]>([], {alias: "existingSettings"})

    readonly $selectedExportWorkflow = signal(PbrExport.Config.defaultExportWorkflow)
    readonly $selectedExportEngine = signal(PbrExport.Config.defaultExportEngine)
    readonly $selectedExportNormalY = signal(PbrExport.Config.defaultExportNormalY)
    // The form's own starting point, not a property of any default export — none of the five is png.
    readonly $selectedExportFormat = signal<MaterialMapsExporterCore.Types.Format>("png")
    readonly $selectedExportResolution = signal<MaterialMapsExporterCore.Types.Resolution>("original")
    exportWorkflows = PbrExport.Types.workflows
    exportEngines = PbrExport.Types.engines
    exportNormalsY = MaterialMapsExporterCore.Types.normalsY
    exportFormats = MaterialMapsExporterCore.Types.formats
    exportResolutions = MaterialMapsExporterCore.Types.resolutions

    /**
     * Compared through the same function the server matches identity with. A guard that computed
     * identity differently would disagree in exactly the cases that matter.
     */
    readonly $isDuplicate = computed(() => this.$existingSettings().some((existing) => PbrExport.Config.pbrExportSettingsEqual(existing, this.settings())))

    readonly $isLoading = signal(false)
    timeout?: ReturnType<typeof setTimeout>

    private settings(): PbrExport.Types.Settings {
        return {
            workflow: this.$selectedExportWorkflow(),
            engine: this.$selectedExportEngine(),
            normalY: this.$selectedExportNormalY(),
            format: this.$selectedExportFormat(),
            resolution: this.$selectedExportResolution(),
        }
    }

    async submit() {
        clearTimeout(this.timeout)
        this.$isLoading.set(true)
        await this.$onSubmit()(this.settings())
        this.timeout = setTimeout(() => {
            this.$isLoading.set(false)
        }, 300)
    }

    ngOnDestroy() {
        clearTimeout(this.timeout)
    }

    protected readonly workflowToString = PbrExport.Utils.workflowToString
    protected readonly engineToString = PbrExport.Utils.engineToString
    protected readonly normalYToString = PbrExport.Utils.normalYToString
    protected readonly resolutionToString = PbrExport.Utils.resolutionToString
}
